import dayjs from 'dayjs'
import { ReactElement, useEffect, useMemo } from 'react'
import {
  Box,
  Card,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Skeleton,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchAssignments,
  selectAssignments,
  selectAssignmentsStatus,
} from 'src/v3/profile/profile.slice'
import {
  fetchLinkedServices,
  selectLinkedServices,
  selectLinkedServicesStatus,
} from 'src/v3/manage/manage.slice'
import {
  fetchCourses,
  selectCourses,
  selectCoursesStatus,
} from 'src/v3/self-paced/self-paced.slice'
import AssignmentStats from './components/assignment-stats'
import AssignmentCard from './components/assignment-card'
import useAssignmentFilters from './hooks/use-assignment-filters'
import { AssignmentFilterStatus, AssignmentWithCourses } from './types'

export default function AssignmentsV4(): ReactElement {
  let dispatch = useAppDispatch()
  let assignments = useAppSelector(selectAssignments)
  let assignmentsStatus = useAppSelector(selectAssignmentsStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  // Join assignment courses with full course data to get imageName
  let enrichedAssignments: AssignmentWithCourses[] = useMemo(
    () =>
      assignments.map((assignment) => ({
        ...assignment,
        courses: assignment.courses.map((assignmentCourse) => {
          let fullCourse = courses.find((c) => c.id === assignmentCourse.id)
          return {
            id: assignmentCourse.id,
            name: assignmentCourse.name,
            imageName: fullCourse?.imageName,
            status: assignmentCourse.status,
            finalDate: assignmentCourse.finalDate,
          }
        }),
      })),
    [assignments, courses],
  )

  let { filters, actions, filteredAssignments, stats } =
    useAssignmentFilters(enrichedAssignments)

  let { statusFilter, dateLowerFilter, dateUpperFilter } = filters
  let { setStatusFilter, setDateLowerFilter, setDateUpperFilter } = actions

  let linkedServiceMap = useMemo(
    () => new Map(linkedServices.map((ls) => [ls.id, ls.name])),
    [linkedServices],
  )

  let isLoading = assignmentsStatus === 'pending' || coursesStatus === 'pending'

  // Always fetch fresh assignment completion data on page load
  useEffect(() => {
    dispatch(fetchAssignments())
  }, [])

  // Fetch metadata only if not already loaded
  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [linkedServicesStatus, coursesStatus])

  return (
    <Container maxWidth="xl" className="py-6">
      <Typography variant="h4" component="h1" className="mb-2">
        Assignments
      </Typography>
      <Typography color="text.secondary" variant="body1" className="mb-6">
        Track and complete your assigned courses
      </Typography>
      <AssignmentStats stats={stats} />
      {/* Filters */}
      <Card
        className="p-6 mb-6"
        sx={{ bgcolor: 'background.paper' }}
        component="section"
        aria-label="Assignment filters"
      >
        <Box
          className="grid grid-cols-1 gap-4 md:grid-cols-3"
          role="group"
          aria-label="Assignment filters"
        >
          <FormControl fullWidth sx={{ backgroundColor: 'background.paper' }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as AssignmentFilterStatus)
              }
            >
              <MenuItem value="All">All Assignments</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Overdue">Overdue</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={dateLowerFilter ? dayjs(dateLowerFilter) : null}
              onChange={(newValue) =>
                setDateLowerFilter(
                  newValue ? newValue.format('YYYY-MM-DD') : null,
                )
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { bgcolor: 'background.paper' },
                },
              }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To Date"
              value={dateUpperFilter ? dayjs(dateUpperFilter) : null}
              onChange={(newValue) =>
                setDateUpperFilter(
                  newValue ? newValue.format('YYYY-MM-DD') : null,
                )
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { bgcolor: 'background.paper' },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Card>
      {/* Assignment List */}
      <Box className="mb-6" component="section" aria-label="Assignment list">
        <Typography variant="h5" className="mb-4" id="assignments-heading">
          Your Assignments ({filteredAssignments.length})
        </Typography>

        {isLoading ? (
          <Box
            className="grid grid-cols-1 gap-4"
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label="Loading assignments"
          >
            {Array.from({ length: 3 }).map((_, index) => (
              <Card key={index} sx={{ p: 3 }}>
                <Box className="flex items-start gap-3">
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: 2,
                      flexShrink: 0,
                    }}
                  />
                  <Box className="flex-1 min-w-0">
                    <Skeleton
                      variant="text"
                      sx={{
                        width: '60%',
                        height: 32,
                        mb: 1,
                      }}
                    />
                    <Box className="flex flex-wrap items-center gap-2 mb-2">
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          width: 80,
                          height: 24,
                          borderRadius: 12,
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          width: 140,
                          height: 20,
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                    <Skeleton
                      variant="text"
                      sx={{
                        width: '40%',
                        height: 20,
                        mb: 2,
                      }}
                    />
                    <Box className="mt-3">
                      <Box className="flex items-center justify-between mb-1">
                        <Skeleton
                          variant="text"
                          sx={{
                            width: 60,
                            height: 20,
                          }}
                        />
                        <Skeleton
                          variant="text"
                          sx={{
                            width: 100,
                            height: 20,
                          }}
                        />
                      </Box>
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          width: '100%',
                          height: 8,
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                  </Box>
                  <Skeleton
                    variant="circular"
                    sx={{
                      width: 24,
                      height: 24,
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Card>
            ))}
            <span className="sr-only">Loading assignments...</span>
          </Box>
        ) : filteredAssignments.length === 0 ? (
          <Card className="p-12 text-center" role="status" aria-live="polite">
            <Typography color="text.secondary" variant="h6" className="mb-2">
              No assignments found
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {statusFilter !== 'All'
                ? 'Try adjusting your filters to see more assignments'
                : 'You have no assignments at this time'}
            </Typography>
          </Card>
        ) : (
          <Box
            className="grid grid-cols-1 gap-4"
            role="list"
            aria-labelledby="assignments-heading"
          >
            {filteredAssignments.map((assignment) => (
              <Box key={assignment.id} role="listitem">
                <AssignmentCard
                  assignment={assignment}
                  linkedServiceName={linkedServiceMap.get(
                    assignment.linkedServiceID,
                  )}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Container>
  )
}
